import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { Box, useTheme } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';

import { CameraStateContext } from 'context/state/camera';

dayjs.extend(duration);

const formatTimeSince = (unixTimestamp: number) => {
  const now = dayjs();
  const startTime = dayjs.unix(unixTimestamp);
  const diffInSeconds = now.diff(startTime, 'second');

  const timeDuration = dayjs.duration(diffInSeconds, 'seconds');

  const hours = Math.floor(timeDuration.hours());
  const minutes = Math.floor(timeDuration.minutes());
  const seconds = timeDuration.seconds();

  return [hours, minutes, seconds].map((v) => v.toString().padStart(2, '0')).join(':');
};

const TimeSince = ({ unixTimestamp }: { unixTimestamp: number }) => {
  const [elapsedTime, setElapsedTime] = useState(formatTimeSince(unixTimestamp));

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime(formatTimeSince(unixTimestamp));
    }, 1000);

    return () => clearInterval(interval);
  }, [unixTimestamp]);

  return `${elapsedTime}`;
};

export const RecordingIndicator = () => {
  const { recording } = useContext(CameraStateContext);
  const theme = useTheme();

  const styles = {
    recordingIndicator: {
      position: 'absolute',
      top: '6%',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '3',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      color: theme.palette.primary.contrastText,
      padding: '0.313rem 0.75rem',
      borderRadius: '624rem',
      border: `0.063rem solid ${theme.palette.primary.contrastText}`,

      // The next properties is to make sure the dot and text are aligned
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      alignItems: 'center',
      gap: '8px',
    },
    recordingIndicator__dot: {
      borderRadius: '624rem',
      width: '1rem',
      height: '1rem',
      background: theme.palette.error.main,
      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    },
    recordingIndicator__text: {
      fontSize: '1.5rem',
      lineHeight: '150%',
      color: theme.palette.primary.contrastText,
    },
  };

  return (
    <Box data-testid="component-indicator-recording" sx={{ ...styles.recordingIndicator }}>
      <Box sx={{ ...styles.recordingIndicator__dot }} />
      <Box component="span" sx={{ ...styles.recordingIndicator__text }}>
        <TimeSince unixTimestamp={recording.startTime as number} />
      </Box>
    </Box>
  );
};
